import React, { useEffect, useState, useCallback } from 'react';
import { toast } from '@/components/ui/use-toast';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/lib/supabaseClient';
import { Fixture as BaseFixture } from '@/types/fixtures'; // Ensure this import is correct
import { Team } from 'src/types/teams'; // Ensure this import is correct

interface TeamWithNominations extends Team {
  nominations_locked?: boolean;
  is_lowest_team: boolean;
}
import FixtureItem from './FixtureItem';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { MoreHorizontal, CalendarDays } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';

type Club = {
  id: string;
  name: string;
  logo_path: string;
};

interface FixtureWithOptionalEdit extends BaseFixture {
  allowEdit?: boolean;
}

interface FixturesListProps {
  selectedDivision: string;
  selectedTeam: string;
  seasonId: string;
  fixtures: FixtureWithOptionalEdit[];
  onEnterResult: (fixture: FixtureWithOptionalEdit) => void;
  refreshKey?: boolean;
  onEditFixture?: (fixture: BaseFixture) => void;
  onDeleteFixture?: () => void;
  teams: TeamWithNominations[];
  clubs: Club[]; // Added clubs prop
  projectSlug: string; // <- Add this line
}

const FixturesList: React.FC<FixturesListProps> = ({
  selectedDivision,
  selectedTeam,
  seasonId,
  onEnterResult,
  refreshKey,
  onEditFixture,
  onDeleteFixture,
  teams, // Updated to receive teams prop from parent
  clubs, // Added clubs destructure
}) => {
  const { isAdmin, userClubId } = useAuth();
  const [loading, setLoading] = useState(true);

  const [missingLocksModalOpen, setMissingLocksModalOpen] = useState(false);
  const [missingLocksMessage, setMissingLocksMessage] = useState('');

  const selectedTeamName = teams.find((team) => team.id === selectedTeam)?.name ?? '';
  const resolvedProjectId = teams.find((t) => t.id === selectedTeam)?.project_id;

  // State to hold the fixtures
  const [fixturesData, setFixtures] = useState<FixtureWithOptionalEdit[]>([]);

  const fetchFixtures = useCallback(async () => {
    if (!resolvedProjectId) {
      setFixtures([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const { data, error } = await supabase
      .from('fixtures')
      .select(`
        id,
        match_date,
        tee_time,
        status,
        completed,
        created_at,
        season_id,
        division_id,
        result,
        home_team:home_team_id (
          id,
          name,
          club_id,
          division_id,
          division:division_id (
            id,
            name
          )
        ),
        away_team:away_team_id (
          id,
          name,
          club_id,
          division_id,
          division:division_id (
            id,
            name
          )
        ),
        project_id,
        home_team_id,
        away_team_id,
        home_team_points,
        away_team_points
      `)
      .eq('season_id', seasonId)
      .eq('project_id', resolvedProjectId || '')
      .order('match_date', { ascending: true });

      console.log('🧪 Raw data from Supabase:', data);
      console.log('📦 Resolved project ID:', resolvedProjectId);
      console.log('🧪 Season ID:', seasonId);
  
    if (error) {
      console.error('❌ Error fetching fixtures:', error.message);
      setFixtures([]);
    } else {
      const parsedFixtures: FixtureWithOptionalEdit[] = (data ?? []).map((item: any) => ({
        id: item.id,
        match_date: item.match_date,
        tee_time: item.tee_time ?? '17:00',
        status: item.status ?? 'scheduled',
        completed: item.completed ?? false,
        created_at: item.created_at,
        season_id: item.season_id,
        division_id: item.division_id,
        result: item.result,
        home_team_id: item.home_team_id ?? '', // Ensure this property exists
        away_team_id: item.away_team_id ?? '', // Ensure this property exists
        home_team_points: item.home_team_points ?? null, // Add the missing property
        away_team_points: item.away_team_points ?? null, // Add the missing property
        project_id: item.project_id,
        home_team: { 
          id: item.home_team_id ?? '', 
          name: item.home_team?.name ?? '', 
          club_id: item.home_team?.club_id ?? '', 
          division_id: item.home_team?.division_id ?? '', 
          project_id: item.project_id,
          division: { id: item.home_team?.division_id ?? '', name: item.home_team?.division?.name ?? '' } // Add the division property
        },
        away_team: { 
          id: item.away_team_id ?? '', 
          name: item.away_team?.name ?? '', 
          club_id: item.away_team?.club_id ?? '', 
          division_id: item.away_team?.division_id ?? '', 
          project_id: item.project_id,
          division: { id: item.away_team?.division_id ?? '', name: item.away_team?.division?.name ?? '' } // Add the division property
        },
      }));
  
      setFixtures(parsedFixtures); // Ensure the state is updated with the correct data type
    }
  
    setLoading(false);
  }, [seasonId, resolvedProjectId]);

  useEffect(() => {
    fetchFixtures();
  }, [fetchFixtures, refreshKey]);

  const filteredFixtures = fixturesData.filter(
    (fixture) =>
      fixture.home_team.division?.id === selectedDivision &&
      (fixture.home_team.id === selectedTeam || fixture.away_team.id === selectedTeam)
  );
  console.log('🔍 All fixtures fetched:', fixturesData);
  console.log('📌 Selected division:', selectedDivision);
  console.log('📌 Selected team:', selectedTeam);

  const canEditFixture = (fixture: BaseFixture) => {
    return (
      isAdmin ||
      fixture.home_team.club_id === userClubId
    );
  };

  const highlightTeam = (name: string) =>
    name === selectedTeamName ? (
      <span className="font-semibold text-foreground">{name}</span>
    ) : (
      <span className="text-muted-foreground">{name}</span>
    );

  const handleDelete = async (fixtureId: string) => {
    const confirmed = window.confirm('Are you sure you want to delete this fixture?');
    if (!confirmed) return;

    const { error } = await supabase.from('fixtures').delete().eq('id', fixtureId);
    if (error) {
      console.error('❌ Error deleting fixture:', error.message);
    } else {
      onDeleteFixture?.();
    }
  };

  return (
    <>
      <Dialog open={missingLocksModalOpen} onOpenChange={setMissingLocksModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="mb-2">Cannot enter result</DialogTitle>
            <DialogDescription>
              <span dangerouslySetInnerHTML={{ __html: missingLocksMessage }} />
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    <Card>
      <CardHeader className="bg-muted/50 pb-3">
        <CardTitle className="text-xl font-medium flex items-center gap-2">
          <CalendarDays className="h-6 w-6 text-muted-foreground" />
          {selectedTeam && teams.find((team) => team.id === selectedTeam)?.name}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="p-4 text-sm text-muted-foreground">Loading fixtures...</div>
        ) : filteredFixtures.length === 0 ? (
          <div className="p-4 text-sm text-muted-foreground">No fixtures found.</div>
        ) : (
          <ul className="divide-y divide-gray-300">
            {filteredFixtures.map((fixture) => (
              <li
                key={fixture.id}
                className={cn(
                  'relative border border-gray-200 rounded-md',
                  fixture.status === 'scheduled' && !(isAdmin || fixture.home_team.club_id === userClubId) && 'pb-10'
                )}
              >
                <FixtureItem
                  {...fixture as BaseFixture}
                  highlightTeamName={selectedTeamName}
                  onClick={async () => {
                    if (fixture.status === 'completed') {
                      onEnterResult({ ...(fixture as FixtureWithOptionalEdit), allowEdit: false });
                    } else if (
                      fixture.status === 'scheduled' &&
                      (isAdmin || fixture.home_team.club_id === userClubId)
                    ) {
                      const { data: teamData, error } = await supabase
                        .from('teams')
                        .select('id, name, nominations_locked, is_lowest_team')
                        .in('id', [fixture.home_team.id, fixture.away_team.id]);

                      if (error) {
                        console.error('Error fetching team nomination data:', error.message);
                        return;
                      }

                      const homeTeam = teamData.find((t: any) => t.id === fixture.home_team.id);
                      const awayTeam = teamData.find((t: any) => t.id === fixture.away_team.id);
                      const missingLocks: string[] = [];

                      if (homeTeam && !homeTeam.is_lowest_team && !homeTeam.nominations_locked) {
                        missingLocks.push(homeTeam.name || 'Home Team');
                      }
                      if (awayTeam && !awayTeam.is_lowest_team && !awayTeam.nominations_locked) {
                        missingLocks.push(awayTeam.name || 'Away Team');
                      }

                      if (missingLocks.length > 0) {
                        setMissingLocksMessage(
                          `The following team(s) have not locked in their nominations: ${missingLocks
                            .map((name) => `<span style="color: #30702F; font-weight: bold;">${name}</span>`)
                            .join(', ')}.<br />Navigate to the teams section to manage nominated players.`
                        );
                        setMissingLocksModalOpen(true);
                        return;
                      }

                      onEnterResult({ ...(fixture as FixtureWithOptionalEdit), allowEdit: true });
                    }
                  }}
                />
                {fixture.status === 'completed' && (
                  <div className="p-2">
                    <Button
                      size="sm"
                      className="bg-transparent border border-gray-300 px-3 text-black hover:bg-[#30702F] hover:text-white"
                      onClick={() => onEnterResult({ ...(fixture as FixtureWithOptionalEdit), allowEdit: false })}
                    >
                      View Result
                    </Button>
                  </div>
                )}

                {fixture.status === 'scheduled' && (isAdmin || fixture.home_team.club_id === userClubId) && (
                  <div className="p-2">
                    <Button
                      size="sm"
                      className="bg-[#30702F] hover:bg-[#245723] text-white"
                      onClick={async () => {
                        const checkNominationsLocked = async () => {
                          const { data: teamData, error } = await supabase
                            .from('teams')
                            .select('id, name, nominations_locked, is_lowest_team')
                            .in('id', [fixture.home_team.id, fixture.away_team.id]);

                          if (error) {
                            console.error('Error fetching team nomination data:', error.message);
                            return;
                          }

                          const homeTeam = teamData.find((t: any) => t.id === fixture.home_team.id);
                          const awayTeam = teamData.find((t: any) => t.id === fixture.away_team.id);
                          const missingLocks: string[] = [];

                          if (homeTeam && !homeTeam.is_lowest_team && !homeTeam.nominations_locked) {
                            missingLocks.push(homeTeam.name || 'Home Team');
                          }
                          if (awayTeam && !awayTeam.is_lowest_team && !awayTeam.nominations_locked) {
                            missingLocks.push(awayTeam.name || 'Away Team');
                          }

                          if (missingLocks.length > 0) {
                            setMissingLocksMessage(
                              `The following team(s) have not locked in their nominations: ${missingLocks
                                .map((name) => `<span style="color: #30702F; font-weight: bold;">${name}</span>`)
                                .join(', ')}.<br />Navigate to the teams section to manage nominated players.`
                            );
                            setMissingLocksModalOpen(true);
                            return;
                          }

                          onEnterResult({ ...(fixture as FixtureWithOptionalEdit), allowEdit: true });
                        };
                        checkNominationsLocked();
                      }}
                    >
                      Enter Result
                    </Button>
                  </div>
                )}
                {(canEditFixture(fixture) && (isAdmin || fixture.status === 'scheduled')) && (
                  <div className="absolute top-2 right-2 z-10">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button size="icon" variant="ghost">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {fixture.status === 'scheduled' ? (
                          <DropdownMenuItem onClick={() => onEditFixture?.(fixture)}>
                            Edit Fixture
                          </DropdownMenuItem>
                        ) : (
                          isAdmin && fixture.status === 'completed' && (
                            <DropdownMenuItem
                              onClick={() => {
                                const editableFixture = {
                                  ...(fixture as FixtureWithOptionalEdit),
                                  allowEdit: true,
                                };
                                onEnterResult(editableFixture);
                              }}
                            >
                              Edit Result
                            </DropdownMenuItem>
                          )
                        )}
                        <DropdownMenuItem
                          className="text-destructive"
                          onClick={() => handleDelete(fixture.id)}
                        >
                          Delete Fixture
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                )}
                <div className="absolute bottom-2 right-2 flex items-center gap-2 text-sm pb-1">
                  <span
                    className={cn(
                      'w-2.5 h-2.5 rounded-full',
                      fixture.status === 'completed' ? 'bg-green-600' : 'bg-yellow-500'
                    )}
                  ></span>
                  <span
                    className={cn(
                      'font-medium',
                      fixture.status === 'completed' ? 'text-green-700' : 'text-yellow-700'
                    )}
                  >
                    {fixture.status === 'completed' ? 'Completed' : 'Scheduled'}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
    </>
  );
};

export default FixturesList;